import React, { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { fetchBookings, fetchHotelsForBooking } from '../services/api'
import { Calendar, Hotel, Users, Search, Filter, Clock, CheckCircle, XCircle } from 'lucide-react'
import Select from 'react-select'

const BookingContent = ({ activeTab }) => {
  const [filters, setFilters] = useState({
    dateRange: { 
      start: new Date().toISOString().split('T')[0],
      end: new Date().toISOString().split('T')[0]
    },
    hotelId: 'all',
    status: 'all',
    search: ''
  })

  // Map tab to filter parameters
  const getTabFilters = (tab) => {
    const today = new Date().toISOString().split('T')[0]
    const baseFilters = { ...filters }
    
    switch (tab) {
      case 'bookings-today':
        // Recent bookings: show top 20 latest, no date constraint
        return { ...baseFilters, tab: undefined, dateRange: null, per_page: 20 }
      case 'bookings-upcoming':
        return { ...baseFilters, tab: 'upcoming' }
      case 'bookings-checkin':
        return { ...baseFilters, tab: 'checkin', dateRange: { start: today, end: today } }
      case 'bookings-checkout':
        return { ...baseFilters, tab: 'checkout', dateRange: { start: today, end: today } }
      default:
        return baseFilters
    }
  }

  const tabFilters = getTabFilters(activeTab)

  // Helper to format FE filters to API params
  const mapStatusToApi = (status) => {
    const v = (status || '').toLowerCase()
    if (v === 'confirmed') return 'scheduled'
    return v || 'all'
  }

  const formatFiltersForApi = (f) => {
    const selectedStatus = (f?.status || 'all').toLowerCase()
    const isPaymentStatus = ['completed', 'unpaid', 'draft'].includes(selectedStatus)
    return {
      date_from: f?.dateRange?.start,
      date_to: f?.dateRange?.end,
      property_id: f?.hotelId && f?.hotelId !== 'all' ? f.hotelId : undefined,
      payment_status: isPaymentStatus ? selectedStatus : undefined,
      search: f?.search,
      tab: f?.tab,
      per_page: f?.per_page,
    }
  }

  // Fetch bookings data
  const { data: bookingsData, isLoading: isBookingsLoading, error } = useQuery({
    queryKey: ['bookings', activeTab, tabFilters],
    queryFn: () => fetchBookings(formatFiltersForApi(tabFilters)),
    refetchInterval: 30000, // Refresh every 30 seconds
  })

  // Fetch hotels for filter dropdown
  const { data: hotelsData } = useQuery({
    queryKey: ['hotels-for-booking'],
    queryFn: fetchHotelsForBooking,
    staleTime: 300000, // Hotels don't change often - 5 minutes
  })

  // Build options for searchable hotel dropdown
  const hotelOptions = useMemo(() => {
    const base = [{ value: 'all', label: 'All Hotels' }]
    if (!hotelsData || !Array.isArray(hotelsData) || hotelsData.length === 0) return base
    return base.concat(hotelsData.map(hotel => ({
      value: String(hotel.id),
      label: hotel.name || `Hotel #${hotel.id}`
    })))
  }, [hotelsData])

  const selectedHotelOption = useMemo(() => (
    hotelOptions.find(opt => opt.value === filters.hotelId) || hotelOptions[0]
  ), [hotelOptions, filters.hotelId])

  const getTabTitle = (tab) => {
    switch (tab) {
      case 'bookings-today':
        return 'Recent Bookings'
      case 'bookings-upcoming':
        return 'Upcoming Bookings'
      case 'bookings-checkin':
        return 'Check-ins Today'
      case 'bookings-checkout':
        return 'Check-outs Today'
      default:
        return 'Bookings'
    }
  }

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'confirmed':
      case 'paid':
      case 'completed':
      case 'scheduled':
        return <CheckCircle className="w-4 h-4 text-green-500" />
      case 'pending':
      case 'processing':
        return <Clock className="w-4 h-4 text-yellow-500" />
      case 'cancelled':
        return <XCircle className="w-4 h-4 text-red-500" />
      default:
        return <Clock className="w-4 h-4 text-gray-500" />
    }
  }

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'confirmed':
      case 'paid':
      case 'completed':
      case 'scheduled':
        return 'bg-green-100 text-green-800'
      case 'pending':
      case 'processing':
        return 'bg-yellow-100 text-yellow-800'
      case 'cancelled':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('id-ID', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    })
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount)
  }

  // Filter bookings based on search and filters (with recent-first sorting and top-20 limit for 'bookings-today')
  const filteredBookings = useMemo(() => {
    if (!bookingsData?.bookings) return []
    const toTs = (b) => {
      const d = b?.created_at || b?.schedule_date || b?.updated_at
      return d ? new Date(d).getTime() : 0
    }
    const statusFilter = (filters.status || 'all').toLowerCase()
    const hotelFilter = filters.hotelId
    const filtered = bookingsData.bookings.filter(booking => {
      const matchesSearch = !filters.search || 
        booking.guest_name?.toLowerCase().includes((filters.search || '').toLowerCase()) ||
        booking.code?.toLowerCase().includes((filters.search || '').toLowerCase())
      const matchesPaymentStatus = statusFilter === 'all' || (booking.payment_status?.toLowerCase() === statusFilter)
      const bookingHotelId = String(booking.property_id ?? booking.propertyId ?? booking.property?.id ?? '')
      const matchesHotel = hotelFilter === 'all' || bookingHotelId === String(hotelFilter)
      return matchesSearch && matchesPaymentStatus && matchesHotel
    })
    const sorted = filtered.sort((a, b) => toTs(b) - toTs(a))
    return activeTab === 'bookings-today' ? sorted.slice(0, 20) : sorted
  }, [bookingsData?.bookings, filters, activeTab])

  if (isBookingsLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        <span className="ml-2 text-gray-600">Loading bookings...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center">
          <XCircle className="w-5 h-5 text-red-500 mr-2" />
          <span className="text-red-700">Error loading bookings: {error.message}</span>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">{getTabTitle(activeTab)}</h2>
        <div className="text-sm text-gray-500">
          Total: {filteredBookings.length} bookings
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Hotel Filter */}
          <div className="relative">
            <Select
              options={hotelOptions}
              value={selectedHotelOption}
              onChange={(opt) => setFilters(prev => ({ ...prev, hotelId: opt?.value || 'all' }))}
              isSearchable
              isClearable
              placeholder="All Hotels"
              classNamePrefix="react-select"
            />
          </div>

          {/* Status Filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none"
            >
              <option value="all">All Status</option>
              <option value="completed">Completed</option>
              <option value="unpaid">Unpaid</option>
              <option value="draft">Draft</option>
            </select>
          </div>

          {/* Clear Filters */}
          <button
            onClick={() => setFilters({
              dateRange: { 
                start: new Date().toISOString().split('T')[0],
                end: new Date().toISOString().split('T')[0]
              },
              hotelId: 'all',
              status: 'all',
              search: ''
            })}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Bookings List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {filteredBookings.length === 0 ? (
          <div className="p-8 text-center">
            <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No bookings found</h3>
            <p className="text-gray-500">
              {filters.search || filters.hotelId !== 'all' || filters.status !== 'all'
                ? 'Try adjusting your filters to see more results.'
                : 'No bookings available for the selected period.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Booking Details
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Guest
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Hotel
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Dates
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredBookings.map((booking) => (
                  <tr key={booking.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          #{booking.code || booking.id}
                        </div>
                        <div className="text-sm text-gray-500">
                          Created: {booking.created_at ? formatDate(booking.created_at) : '-'}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {booking.guest_name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {booking.guest_email || ''}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{booking.property_name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm text-gray-900">
                          {booking.schedule_date ? formatDate(booking.schedule_date) : '-'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {/* Additional info like guests could be shown here if available */}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {typeof booking.gross !== 'undefined' ? formatCurrency(booking.gross) : '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(booking.payment_status || booking.status)}`}>
                        {getStatusIcon(booking.payment_status || booking.status)}
                        <span className="ml-1">{booking.payment_status || booking.status}</span>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

export default BookingContent